import { useCallback, useContext, useEffect, useRef } from "react";

import { UseFormReturnType } from "@mantine/form";

import isEqual from "lodash/isEqual";
import isPlainObject from "lodash/isPlainObject";

import { EncounterContext } from "@/contexts/encounter";

/** Deep-normalize for unsaved comparison: "", null, empty arrays, and empty plain objects → undefined. */
const normalize = (value: unknown): unknown => {
  if (value === "" || value === null) return undefined;

  if (Array.isArray(value)) {
    const normalized = value.map((item) => normalize(item)).filter((item) => item !== undefined);
    return normalized.length === 0 ? undefined : normalized;
  }

  if (isPlainObject(value)) {
    const normalizedObject = value as Record<string, unknown>;
    const result: Record<string, unknown> = {};
    for (const key of Object.keys(normalizedObject)) {
      const normalizedValue = normalize(normalizedObject[key]);
      if (normalizedValue !== undefined) result[key] = normalizedValue;
    }
    return Object.keys(result).length === 0 ? undefined : result;
  }

  return value;
};
const normalizeRecord = (
  val: Record<string, unknown> | undefined | null,
): Record<string, unknown> => val ?? {};

const useDrawerUnsavedChanges = <T extends Record<string, unknown>>(
  key: string,
  form: UseFormReturnType<T>,
  serverValues: T,
) => {
  const { registerUnsavedCheck, unregisterUnsavedCheck } = useContext(EncounterContext);
  const serverValuesRef = useRef<T>(serverValues);

  useEffect(() => {
    registerUnsavedCheck?.(key, () => {
      const current = normalizeRecord(form.values);
      const saved = normalizeRecord(serverValuesRef.current);
      const allKeys = new Set([...Object.keys(current), ...Object.keys(saved)]);

      for (const key of allKeys) {
        if (!isEqual(normalize(current[key]), normalize(saved[key]))) return true;
      }
      return false;
    });
    return () => unregisterUnsavedCheck?.(key);
  }, [key, form, registerUnsavedCheck, unregisterUnsavedCheck]);

  const markAsSaved = useCallback(() => {
    serverValuesRef.current = structuredClone(form.values);
  }, [form]);

  const markValuesAsSaved = useCallback((values: T) => {
    serverValuesRef.current = structuredClone(values);
  }, []);

  return { markAsSaved, markValuesAsSaved };
};

export default useDrawerUnsavedChanges;
